/*----------------------------------
- LANDING PAGE CONTENT CATALOG (SSOT)
-----------------------------------*/

import type { IconName } from '@uniquedomains/components/ui/icon';
import { encodeRadarFiltersUrl } from '@/common/libs/filters';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import { isGlobalDomainNavigationTargetPath } from '@/common/catalogs/landing/internalLinks';
import {
    DOMAINS_POPULAR_FILTER_PRESET_CATALOG,
    getPublicFilterPresetHref,
} from '@/common/catalogs/domains/filterPresets';
import { LANDING_MARKET_MENU_ACTIVE_PATH_PREFIXES } from '@/client/catalogs/landing/marketMenu';
import { LANDING_TOOLS_MENU_ACTIVE_PATH_PREFIXES } from '@/client/catalogs/landing/toolsMenu';
import { EDITION_PAGES } from '@uniquedomains/components/contracts/editions/public';
import type { PublishedNewsMenuPreview } from '@uniquedomains/components/contracts/news/public';
import { PUBLIC_PLAN_FACTS } from '@uniquedomains/components/pricing/catalogs/pricingPlans';
import { REFUND_PROMISE_LINE } from '@uniquedomains/components/pricing/catalogs/refundPromise';
import {
    buildPublicSearchHumanPath,
    normalizePublicSearchSectorSlug,
    normalizePublicSearchTldSlug,
} from '@uniquedomains/core/domains/public-search';

/*----------------------------------
- TYPES
-----------------------------------*/

export type LandingPersonaId = 'investor' | 'founder' | 'studio';

export type LandingPersona = {
    id: LandingPersonaId;
    label: string;
    chipLabel: string;
    description: string;
    routeTitle: string;
    routeCopy: string;
    ctaLabel: string;
    defaultPrompt: string;
    helper: string;
};

export type LandingHeaderNavPanelItem = {
    label: string;
    description: string;
    iconName: IconName;
    href?: string;
    count?: number | null;
};

export type LandingHeaderNavPanelColumn = {
    title: string;
    items: readonly LandingHeaderNavPanelItem[];
};

export type LandingHeaderMenuLinkId =
    | 'pricing'
    | 'productAppraise'
    | 'productDomains'
    | 'trendingDomains'
    | 'businessIdeas'
    | 'productRadars'
    | 'productSearch'
    | 'productVerify'
    | 'productWatchlists'
    | 'screener'
    | 'solutionFounder'
    | 'solutionInvestor'
    | 'solutionStudio';

/**
 * A product feature page, listed in the footer. The header stopped carrying a
 * Product menu on 2026-09-17, so these pages are reached from the footer, from the
 * home page workflow section and from the pages that link one another.
 */
export type LandingProductFooterItem = {
    id: string;
    label: string;
    linkId: LandingHeaderMenuLinkId;
};

/** Same story for the three audience pages: footer only since 2026-09-17. */
export type LandingSolutionFooterItem = {
    id: string;
    footerLabel: string;
    linkId: LandingHeaderMenuLinkId;
};

export type LandingHeaderDomainsNavItem = {
    id: 'domains';
    label: string;
    kind: 'mega';
    menu: 'domains';
    href: string;
    columns: readonly LandingHeaderNavPanelColumn[];
    activePathPrefixes?: readonly string[];
};

export type LandingHeaderNavItem =
    | LandingHeaderDomainsNavItem
    | {
          id: 'market' | 'tools';
          label: string;
          kind: 'mega';
          menu: 'market' | 'tools';
          /**
           * Neither Market nor Tools has a hub page. Their triggers are disclosure
           * buttons, never anchors to a non-existent URL or to `#`, so `href` is absent
           * by construction.
           */
          href?: undefined;
          activePathPrefixes: readonly string[];
      }
    | {
          id: string;
          label: string;
          kind: 'static';
          href?: string;
          activePathPrefixes?: readonly string[];
      };

export type LandingHeaderDomainsNavigation = {
    tlds: ReadonlyArray<
        | string
        | {
              extension: string;
              count?: number | null;
          }
    >;
    sectors: ReadonlyArray<{
        name: string;
        slug: string;
        count?: number | null;
    }>;
    totals?: {
        all?: number | null;
        expired?: number | null;
        expiring?: number | null;
        /** Distinct TLDs tracked. Optional: an older Product build omits it. */
        tlds?: number | null;
        /** Names whose availability was rechecked in the last 24 hours. */
        checkedLast24h?: number | null;
    };
    /**
     * Published news editions. The Market menu reads `latest.dateKey` alone, to date its
     * "News updated" line; everything else in the preview stays unread here.
     */
    news?: PublishedNewsMenuPreview;
};

/*----------------------------------
- NAVIGATION
-----------------------------------*/

export const LANDING_DOMAINS_FOOTER_ITEMS = [
    { label: 'All domains', href: '/domains' },
    // The TLD hub replaced the `/domains/tld/*` listings; the footer is its crawlable link from
    // every page, as the header's "See all TLDs" is inside the Domains panel.
    { label: 'All TLDs', href: LANDING_WEBSITE_PAGE_ROUTES.tldHub },
    // The sales page's crawlable link from every page (ADR-0067).
    { label: 'Domain sales', href: LANDING_WEBSITE_PAGE_ROUTES.sales },
    // The auctions page's crawlable link from every page (ADR-0076).
    { label: 'Domain auctions', href: LANDING_WEBSITE_PAGE_ROUTES.auctions },
    // The registrar hub's crawlable link from every page (ADR-0079).
    { label: 'Registrar prices', href: LANDING_WEBSITE_PAGE_ROUTES.registrars },
    { label: 'Brandable', href: '/domains/brandable' },
    { label: 'Short', href: '/domains/short' },
    { label: 'Positive', href: '/domains/positive' },
    { label: 'Domain hacks', href: '/domains/hacks' },
    { label: 'Actionable', href: '/domains/actionable' },
    { label: 'Expiring', href: '/domains/expiring' },
    { label: 'Expired', href: '/domains/expired' },
] as const;

/**
 * The seven product feature pages, in footer order. The row copy and icons this
 * catalog used to carry belonged to the header Product menu, which was removed on
 * 2026-09-17: the footer renders a label and a link, so that is all it holds.
 */
export const LANDING_PRODUCT_FOOTER_ITEMS: readonly LandingProductFooterItem[] = [
    { id: 'search', label: 'Search', linkId: 'productSearch' },
    // The daily editions (ADR-0074), which replaced the Opportunities feature page.
    { id: 'trending-domains', label: EDITION_PAGES.investor.name, linkId: 'trendingDomains' },
    { id: 'business-ideas', label: EDITION_PAGES.founder.name, linkId: 'businessIdeas' },
    { id: 'verify', label: 'Verify', linkId: 'productVerify' },
    { id: 'radars', label: 'Radars', linkId: 'productRadars' },
    { id: 'watchlists', label: 'Watchlists', linkId: 'productWatchlists' },
    { id: 'appraise', label: 'Appraise', linkId: 'productAppraise' },
    // "Find buyers" was listed here with a Soon badge and an anchor back to this
    // same menu. It has no implementation at all, so there is nothing to link to.
] as const;

const PUBLIC_DOMAINS_ROOT_HREF = encodeRadarFiltersUrl({}, { surface: 'website' });
/**
 * Seven rows fill a column at the panel's height. In the ending column, the six TLDs pinned at
 * launch leave one row, which goes to the alphabetically first published pin outside the seed
 * (`.si` until another pin sorts before it, ADR-0064); the others stay on the hub.
 */
const DOMAINS_MENU_COLUMN_LIMIT = 7;
/** The three columns of the Domains panel, used as lookup keys and as their own labels. */
export const DOMAINS_MENU_COLUMN_TITLES = {
    lists: 'Lists',
    endings: 'By ending',
    sectors: 'By sector',
} as const;
const DOMAINS_POPULAR_PAGE_ITEMS: readonly LandingHeaderNavPanelItem[] = [
    {
        label: 'All one-word',
        description: 'The whole list',
        iconName: 'search',
        href: PUBLIC_DOMAINS_ROOT_HREF,
    },
    ...DOMAINS_POPULAR_FILTER_PRESET_CATALOG.map((preset) => ({
        label: preset.label,
        description: preset.description,
        iconName: preset.iconName,
        href: getPublicFilterPresetHref(preset.presetName),
    })),
] as const;

const buildTopTldItems = (
    navigation: LandingHeaderDomainsNavigation,
    publishedDomainPaths?: ReadonlySet<string>,
): LandingHeaderNavPanelItem[] => {
    return navigation.tlds.reduce<LandingHeaderNavPanelItem[]>((output, tld) => {
        const extension = typeof tld === 'string' ? tld : tld.extension;
        const label = extension.trim();
        const slug = normalizePublicSearchTldSlug(extension);
        if (!label || !slug || output.length >= DOMAINS_MENU_COLUMN_LIMIT) return output;

        // The TLD's insight page, `/tld/<slug>` (ADR-0054). Product sends the public set only,
        // so this never links a TLD without a page (D31), and never before the page is published.
        const href = buildPublicSearchHumanPath('tld', slug);
        if (publishedDomainPaths && !publishedDomainPaths.has(href)) return output;

        output.push({
            label,
            description: `Registry status, prices, sales and launch dates for .${slug}.`,
            iconName: 'layers',
            href,
            count: typeof tld === 'string' ? null : tld.count,
        });
        return output;
    }, []);
};

const buildTopSectorItems = (
    navigation: LandingHeaderDomainsNavigation,
    publishedDomainPaths?: ReadonlySet<string>,
): LandingHeaderNavPanelItem[] => {
    // No trailing "All sectors" row: no sector directory exists, and the panel already
    // opens on the screener button, which is the one way out of the list.
    return navigation.sectors.reduce<LandingHeaderNavPanelItem[]>((output, sector) => {
        const name = sector.name.trim();
        const slug = normalizePublicSearchSectorSlug(sector.slug);
        if (!name || !slug || output.length >= DOMAINS_MENU_COLUMN_LIMIT) return output;

        const href = buildPublicSearchHumanPath('sector', slug);
        if (publishedDomainPaths && !publishedDomainPaths.has(href)) return output;

        output.push({
            label: name,
            description: `Browse one-word domains shaped by what ${name} buyers are looking for.`,
            iconName: 'columns',
            href,
            count: sector.count,
        });
        return output;
    }, []);
};

export const buildLandingHeaderNavItems = (
    domainsNavigation: LandingHeaderDomainsNavigation,
    publishedDomainPathList?: readonly string[],
): readonly LandingHeaderNavItem[] => {
    const publishedDomainPaths = publishedDomainPathList
        ? new Set(publishedDomainPathList.filter(isGlobalDomainNavigationTargetPath))
        : undefined;
    const navigationItems: LandingHeaderNavItem[] = [];

    if (!publishedDomainPaths || publishedDomainPaths.has(PUBLIC_DOMAINS_ROOT_HREF)) {
        navigationItems.push({
            id: 'domains',
            label: 'Domains',
            kind: 'mega',
            menu: 'domains',
            href: PUBLIC_DOMAINS_ROOT_HREF,
            columns: [
                {
                    title: DOMAINS_MENU_COLUMN_TITLES.lists,
                    items: DOMAINS_POPULAR_PAGE_ITEMS.filter(
                        (item) => !publishedDomainPaths || Boolean(item.href && publishedDomainPaths.has(item.href)),
                    ),
                },
                {
                    title: DOMAINS_MENU_COLUMN_TITLES.endings,
                    items: buildTopTldItems(domainsNavigation, publishedDomainPaths),
                },
                {
                    title: DOMAINS_MENU_COLUMN_TITLES.sectors,
                    items: buildTopSectorItems(domainsNavigation, publishedDomainPaths),
                },
            ],
        });
    }

    navigationItems.push(
        {
            id: 'market',
            label: 'Market',
            kind: 'mega',
            menu: 'market',
            activePathPrefixes: LANDING_MARKET_MENU_ACTIVE_PATH_PREFIXES,
        },
        {
            id: 'tools',
            label: 'Tools',
            kind: 'mega',
            menu: 'tools',
            activePathPrefixes: LANDING_TOOLS_MENU_ACTIVE_PATH_PREFIXES,
        },
        { id: 'pricing', label: 'Pricing', kind: 'static', href: LANDING_WEBSITE_PAGE_ROUTES.pricing },
    );

    return navigationItems;
};

/**
 * Whether a header item is the page being read. `href` covers the exact page and a
 * prefix covers its sub-pages, so News stays lit on an edition or an archive page
 * and Tools stays lit on any page its panel links.
 */
export const isLandingHeaderNavItemCurrent = (item: LandingHeaderNavItem, currentPath: string): boolean => {
    if (!currentPath) return false;
    if (item.href && currentPath === item.href) return true;

    return (item.activePathPrefixes || []).some(
        (prefix) => currentPath === prefix || currentPath.startsWith(`${prefix}/`),
    );
};

/*----------------------------------
- HERO
-----------------------------------*/

export const LANDING_TRUST_STACK = ['GitHub', 'Google', 'LinkedIn', 'Stripe'] as const;

export const LANDING_PERSONAS: readonly LandingPersona[] = [
    {
        id: 'investor',
        label: 'Investor',
        chipLabel: 'I’m investing',
        description: 'Save what you are looking for once, then review fresh one-word opportunities with the evidence behind them.',
        routeTitle: 'Find one-word opportunities with pricing, renewal, risk, and evidence in one place.',
        routeCopy: 'Best when Radars and Watchlists become the routine you repeat every week.',
        ctaLabel: 'Create Radar',
        defaultPrompt: 'Liquid one-word .com under $25k with clean acquisition signals',
        helper: 'Best for investors who want a steady stream of new names, the evidence behind them, and saved searches.',
    },
    {
        id: 'founder',
        label: 'Founder',
        chipLabel: 'I’m naming a product',
        description: 'Turn a product brief into a buyable shortlist you can act on this week.',
        routeTitle: 'Turn a product brief into a buyable shortlist you can move on with this week.',
        routeCopy: 'Best when the brief, shortlist, and next step live in one Radar instead of loose tabs.',
        ctaLabel: 'Start a Radar',
        defaultPrompt: 'AI bookkeeping for SMBs with a calm, trustworthy tone',
        helper: 'Best for founders who want clarity fast, not endless inspiration.',
    },
    {
        id: 'studio',
        label: 'Studio',
        chipLabel: 'I’m working for a client',
        description: 'Turn client briefs into shortlist-ready, shareable one-word directions faster.',
        routeTitle: 'Turn client briefs into shortlist-ready, shareable one-word options faster.',
        routeCopy: 'Best when a team shares briefs, invites clients to review, and reuses the same evidence across client briefs.',
        ctaLabel: 'Start from a brief',
        defaultPrompt: 'Client brief: short luxury travel name with global feel',
        helper: 'Best for teams turning briefs into defensible shortlist artifacts.',
    },
] as const;

/** The three audience pages, footer only since the header Solutions menu was removed. */
export const LANDING_SOLUTION_FOOTER_ITEMS: readonly LandingSolutionFooterItem[] = [
    { id: 'investor', footerLabel: 'For investors', linkId: 'solutionInvestor' },
    { id: 'founder', footerLabel: 'For founders', linkId: 'solutionFounder' },
    { id: 'studio', footerLabel: 'For studios', linkId: 'solutionStudio' },
] as const;

export const LANDING_HERO_EXAMPLES = [
    'AI bookkeeping for SMBs',
    'Liquid one-word .com under $25k',
    'Premium wellness brand, calm tone, .ai or .com',
    'Client brief: short luxury travel name with global feel',
] as const;

/*----------------------------------
- DIFFERENTIATION
-----------------------------------*/

export const LANDING_DECISION_PILLARS = [
    {
        title: 'Fit',
        description: 'Does this word actually fit the goal, the brand, or what you set out to buy?',
    },
    {
        title: 'Ownership',
        description: 'Can you really get it, at this price, on this path, with this renewal reality?',
    },
    {
        title: 'Risk',
        description: 'What could make it a bad decision: weak demand, history, legal caution, or pricing surprise?',
    },
] as const;

export const LANDING_WORKFLOW_STEPS = [
    {
        title: 'Search',
        body: 'Search by prompt or filters.',
    },
    {
        title: 'Save as Radar',
        body: 'Save the logic, not just the name.',
    },
    {
        title: 'Track changes',
        body: 'Review only what changed.',
    },
    {
        title: 'Decide faster',
        body: 'Keep notes, evidence, and next steps together.',
    },
    {
        title: 'Manage or launch',
        body: 'Move the winner into a Watchlist or into the finalists of a Radar.',
    },
] as const;

/*----------------------------------
- HOME FINAL CTA
-----------------------------------*/

/**
 * Copy for the home page's closing persona card, per mockup 3a of the
 * "Website Final CTA" design doc.
 *
 * Lives here rather than in the shared persona conversion catalog because none of
 * it is reused by the Product auth modal: the modal opens on its own persona step
 * and never shows bullets or a section headline. Persona identity (title,
 * description, icon) still comes from `CONTEXTUAL_SEARCH_PERSONAS` so the card and
 * the modal that it opens cannot drift apart.
 *
 * `headlineLead` has no count in it: the total is injected at render time from the
 * live stats payload. The mockup's "2.4 million" is a design placeholder.
 */
export const HOME_FINAL_CTA_COPY = {
    headlineLead: 'one-word domains.',
    /** Used when the stats endpoint is unreachable and returns 0. Matches the hero's existing wording. */
    headlineCountFallback: 'Millions of',
    headlineFollowUp: 'Yours is in there.',
    subtitle:
        'Pick how you hunt. Your first search comes back scored for price, demand, history and trademark risk, in about two minutes.',
    // Not "Free to start" and "No card required": the free plan is retired (ADR-0083), so the
    // pills state the one plan and the refund that stands in for a trial.
    reassurance: [`One plan, $${PUBLIC_PLAN_FACTS.member.price} a month`, REFUND_PROMISE_LINE, 'Change your path any time'],
    alreadyMemberCtaLabel: 'Already have an account? Sign in',
} as const;

/**
 * Per-persona card body. Every bullet restates a claim the matching
 * `/solutions/*` page already makes, so the home page never promises more than the
 * page it hands off to: daily Radar alerts and price drops, the ask-against-resale
 * price gap with renewal and risk, brief-to-shortlist Radars, a trademark check.
 */
export const HOME_FINAL_CTA_PERSONA_CARDS = {
    investor: {
        startLabel: 'Start as an investor',
        bullets: [
            'Fresh drops and price cuts every morning',
            'Price gap, renewal and risk in one row',
        ],
    },
    founder: {
        startLabel: 'Start as a founder',
        bullets: [
            'Your brief becomes a buyable shortlist',
            'Trademark risk flagged before you commit',
        ],
    },
} as const satisfies Record<'investor' | 'founder', { startLabel: string; bullets: readonly string[] }>;

/**
 * Sentence that follows the signup count in each social proof row. Both surfaces
 * annotate the same avatar strip with the same live `totalSignups`, so the two
 * wordings are kept side by side to make an accidental divergence obvious.
 */
export const HOME_SOCIAL_PROOF_COPY = {
    hero: 'people have joined Unique Domains',
    finalCta: 'run their domain search here',
} as const;
